import { ReactNode, useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { LayoutDashboard, Users, Building2, Building, LogOut, LogIn, Loader2, Sliders, KeyRound, BookOpen, CalendarCheck, UserCog, ShieldCheck, Package, Menu, Receipt, Wallet, Settings, ClipboardList, Landmark } from "lucide-react";
import { useAuth } from "@/context/auth-context";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useHealthCheck, getHealthCheckQueryKey } from "@workspace/api-client-react";
import { CallbackReminder } from "@/components/callback-reminder";

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [location] = useLocation();
  const { user, logout, isLoading } = useAuth();
  const { data: health } = useHealthCheck({ query: { queryKey: getHealthCheckQueryKey(), refetchInterval: 60000 } });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Close the drawer whenever the route changes.
  useEffect(() => { setMobileNavOpen(false); }, [location]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <>{children}</>;
  }

  const isAdmin = user.role === "super_admin" || user.role === "admin";
  const isSuperAdmin = user.role === "super_admin";
  // Phase 41 Plan 10 (TPRM-05): the four task-config Settings screens are
  // admin+ops_lead only. The server (canEditConfig) is the real authority —
  // this hides the nav entries for everyone else as UX, not security.
  const canEditTaskConfig = isAdmin || user.role === "ops_lead";
  type NavItem = {
    name: string;
    href: string;
    icon: typeof LayoutDashboard;
    show: boolean;
    // Phase 54 Plan 06 (Pitfall 1): optional per-child show flag, defaults to
    // visible (undefined treated as true below) — lets a group be show:true
    // at the top level while still allowing a future role-gated child.
    children?: { name: string; href: string; show?: boolean }[];
  };
  const navigation: NavItem[] = [
    { name: "Dashboard", href: "/", icon: LayoutDashboard, show: true },
    {
      // Phase 41 (v2.0 Task Management Module foundation). 41-10 appends the
      // Settings child links (task_category / sla_policy / routing_rule /
      // escalation_rule CRUD) below, gated to admin+ops_lead (TPRM-05) —
      // front_desk/housekeeping/bde/agent/centralised_team keep New
      // Task + Search only.
      name: "Tasks",
      href: "/tasks",
      icon: ClipboardList,
      show: true,
      children: [
        { name: "New Task", href: "/tasks/new" },
        { name: "Search", href: "/tasks/search" },
        // Phase 43 Plan 06: read-only for every role, same as New Task/Search.
        // Phase 49 Plan 08: renamed "Daily Checklist" -> "Checklist" — the
        // page is now tabbed (Daily/Weekly/Monthly/Onboarding/Exit), same href.
        { name: "Checklist", href: "/tasks/checklist" },
        ...(canEditTaskConfig
          ? [
              { name: "Categories", href: "/tasks/settings/categories" },
              { name: "SLA Policies", href: "/tasks/settings/sla" },
              { name: "Routing Rules", href: "/tasks/settings/routing" },
              { name: "Escalation Rules", href: "/tasks/settings/escalation" },
              { name: "Checklists", href: "/tasks/settings/checklists" },
            ]
          : []),
      ],
    },
    { name: "Calendar", href: "/today", icon: CalendarCheck, show: true },
    { name: "Leads", href: "/leads", icon: Users, show: true },
    {
      name: "Inventory",
      href: "/inventory",
      icon: Package,
      show: true,
      children: [
        { name: "Operation",  href: "/inventory/operation" },
        // Phase 54 Plan 06 (SPACE-UX-HUB-05): the "Space" child is removed —
        // its routes (resources/occupancy) are now reached via the top-level
        // "Spaces" group below. "Space Designer" is deliberately KEPT here
        // (planner's Open Question 6 resolution: two entry points to the
        // Designer is consistent with the hub's own two-entry-points framing).
        { name: "Space Designer", href: "/inventory/space/designer" },
        { name: "Infra",      href: "/inventory/infra" },
        { name: "HSN & GST",  href: "/inventory/hsn-codes" },
        { name: "Charges",    href: "/inventory/charge-types" },
      ],
    },
    {
      name: "Billing",
      href: "/billing",
      icon: Receipt,
      show: true,
      children: [
        { name: "Contracts", href: "/billing/contracts" },
        { name: "Accounts",  href: "/accounts" },
        { name: "Members",   href: "/members" },
        { name: "Invoices",  href: "/billing/invoices" },
        { name: "Plans",     href: "/billing/plans" },
        { name: "Packages",  href: "/billing/packages" },
        { name: "Discounts", href: "/billing/discounts" },
      ],
    },
    { name: "Expenses", href: "/expenses", icon: Wallet, show: true },
    // Phase 57 Plan 16 — the ledger the expense subsystem now posts to:
    // trial balance, account ledger, AP by vendor, P&L, backfill, quarantine,
    // and (super-admin only) master data + the D9 threshold config.
    { name: "Ledger", href: "/ledger", icon: Landmark, show: true },
    { name: "Attendance", href: "/attendance", icon: LogIn, show: true },
    {
      name: "Settings",
      href: "/settings",
      icon: Settings,
      show: isAdmin,
      children: [
        { name: "Receipt & Invoice", href: "/settings/documents" },
        { name: "Resource Pricing",  href: "/settings/resource-pricing" },
      ],
    },
    {
      // Phase 54 Plan 06 (SPACE-UX-HUB-05, Pitfall 1 — non-optional): the flat
      // admin-only "Spaces" item becomes a group absorbing the Occupancy/
      // Resources/Floor-plans surfaces that used to live only under
      // Inventory (which is show: true for every role). show MUST be true
      // here — reusing the old show: isAdmin would silently strip
      // front_desk/housekeeping's existing access to Occupancy/Resources.
      name: "Spaces",
      href: "/spaces",
      icon: Building,
      show: true,
      children: [
        { name: "Centers",     href: "/spaces",                    show: true },
        { name: "Floor plans", href: "/inventory/space/designer",  show: true },
        { name: "Resources",   href: "/inventory/space/resources", show: true },
        { name: "Occupancy",   href: "/inventory/space/occupancy", show: true },
      ],
    },
    { name: "Team", href: "/team", icon: UserCog, show: isAdmin },
    { name: "Login Activity", href: "/login-activity", icon: ShieldCheck, show: isAdmin },
    { name: "Score Builder", href: "/score-builder", icon: Sliders, show: isSuperAdmin },
    { name: "API Keys", href: "/api-keys", icon: KeyRound, show: true },
    { name: "API Docs", href: "/api-docs", icon: BookOpen, show: true },
  ].filter((n) => n.show);

  const navBody = (
    <>
      <div className="flex h-14 items-center border-b px-4">
        <span className="text-lg font-bold text-sidebar-foreground">Coworking Hub</span>
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto px-3 py-4">
        <nav className="flex-1 space-y-1">
          {navigation.map((item) => {
            const isActive = location === item.href || (item.href !== "/" && location.startsWith(item.href));
            const Icon = item.icon;
            if (item.children && item.children.length > 0) {
              // Keep the submenu open whenever the parent OR any of its children
              // is the current route. Some children live outside the parent's
              // prefix (e.g. Billing > Accounts = /accounts, Billing > Members =
              // /members), so the parent's own prefix match isn't enough — match
              // each child's base path (query string ignored, for any child that
              // still carries one).
              const hasActiveChild = item.children.some((c) => {
                const base = c.href.split("?")[0];
                return location === base || location.startsWith(base + "/");
              });
              const expanded = isActive || hasActiveChild;
              return (
                <div key={item.name} className="space-y-1">
                  <Link
                    href={item.href}
                    className={`group flex items-center rounded-md px-2 py-2 text-sm font-medium ${
                      expanded
                        ? "bg-sidebar-primary text-sidebar-primary-foreground"
                        : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    }`}
                  >
                    <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    {item.name}
                  </Link>
                  {expanded && (
                    <div className="ml-8 space-y-1">
                      {item.children.filter((c) => c.show !== false).map((c) => {
                        const childActive = location === c.href || location.startsWith(c.href + "/");
                        return (
                          <Link
                            key={c.href}
                            href={c.href}
                            className={`block rounded-md px-2 py-1.5 text-xs font-medium ${
                              childActive
                                ? "bg-sidebar-primary/30 text-sidebar-primary-foreground"
                                : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                            }`}
                          >
                            {c.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={item.name}
                href={item.href}
                className={`group flex items-center rounded-md px-2 py-2 text-sm font-medium ${
                  isActive
                    ? "bg-sidebar-primary text-sidebar-primary-foreground"
                    : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                }`}
              >
                <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </div>
      <div className="border-t p-4 flex flex-col gap-4">
        <div className="flex items-center text-xs text-muted-foreground px-2">
          <div className={`w-2 h-2 rounded-full mr-2 ${health?.status === "ok" ? "bg-green-500" : "bg-red-500"}`} />
          System Status: {health?.status || "Checking..."}
        </div>
        <div className="flex items-center">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-sidebar-foreground truncate">{user.name}</p>
            <p className="text-xs text-sidebar-foreground/70 capitalize">{user.role.replace("_", " ")}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={logout}>
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <>
    <CallbackReminder />
    {/* 2026-05-20: h-dvh (dynamic viewport height), not h-screen (100vh). On mobile/tablet
        browsers 100vh includes the address-bar area, so the real visible height is taller than
        what's shown and the bottom of the scroll region became unreachable in landscape.
        h-dvh (Tailwind v4 native) tracks the actual visible viewport. Single class to avoid
        an h-screen/h-dvh cascade conflict. */}
    <div className="flex h-dvh overflow-hidden bg-background">
      {/* Desktop sidebar (md+) */}
      <aside className="hidden w-64 border-r bg-sidebar md:flex md:flex-col">
        {navBody}
      </aside>

      {/* Main column */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile top bar (< md) with hamburger */}
        <header className="flex h-14 items-center justify-between border-b bg-background px-3 md:hidden">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 bg-sidebar p-0 flex flex-col">
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation</SheetTitle>
              </SheetHeader>
              {navBody}
            </SheetContent>
          </Sheet>
          <span className="text-base font-semibold">Coworking Hub</span>
          <div className="w-9" />{/* spacer to balance the hamburger */}
        </header>

        {/* pb-8 on md+ instead of pb-24 — the older pb-24 left 96px of
            empty space at the bottom of every page. App-shell pages
            (inventory-operation, etc.) need the height; standard pages
            still get a comfortable 32px gutter. Mobile keeps pb-24 to
            clear the system gesture bar / bottom safe area. */}
        <main className="flex-1 overflow-y-auto p-3 pb-24 sm:p-4 sm:pb-24 md:p-8 md:pb-8">
          {children}
        </main>
      </div>
    </div>
    </>
  );
}
